import { CheckCircle } from '@mui/icons-material'
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { Outlet, useLocation } from 'react-router-dom'

import logoWhite from '~/assets/images/corp/logo-white.svg'
import PageErrorBoundary from '~/components/pageErrorBoundary'
import PartnerThemeWrapper from '~/features/partners/components/partnerThemeWrapper'
import { isLibbyDomain } from '~/features/partners/utils'
import Footer from '~/layouts/auth/footer'
import Logo from '~/layouts/auth/header/logo'
import heyLibbyCapabilities from '~/layouts/auth/utils'
import { TNavigateState } from '~/models/auth'
import rem from '~/utils/ui'

function AuthLayout() {
  const theme = useTheme()
  const { state } = useLocation() as { state: TNavigateState | null }
  const isPartner = !isLibbyDomain()

  const showReplifySections = !(isPartner || state?.isPartnerThemed)

  // Need to decide what to wrap if partner themed
  const renderWrapper = () => {
    if (state?.isPartnerThemed || isPartner) {
      return (
        <PartnerThemeWrapper>
          <PageErrorBoundary>
            <Outlet />
          </PageErrorBoundary>
        </PartnerThemeWrapper>
      )
    }

    return (
      <PageErrorBoundary>
        <Outlet />
      </PageErrorBoundary>
    )
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateRows: '1fr',
        gridAutoFlow: 'row',
        gridAutoRows: 'auto',
        minHeight: '100dvh',
        maxHeight: { md: '100dvh' },
      }}
    >
      <Stack
        direction={{ xxxs: 'column', md: 'row' }}
        useFlexGap
        sx={{ width: '100%', overflow: { md: 'hidden' } }}
      >
        <Stack sx={{ width: '100%', overflow: 'auto' }}>
          <Box sx={{ ml: 2, mt: 1 }}>
            <Logo />
          </Box>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingBlock: { xxxs: rem(100), md: 0 },
              paddingInline: { xxxs: rem(24), md: 0 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'center',
                width: '100%',
                maxWidth: rem(384),
              }}
            >
              {renderWrapper()}
            </Box>
          </Box>
        </Stack>
        {showReplifySections && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: theme.palette.menuBackground,
              width: '100%',
              color: theme.palette.common.white,
              overflow: 'auto',
            }}
          >
            <Stack
              sx={{
                width: '100%',
                maxWidth: rem(400),
                p: { xxxs: 4, md: 0 },
                gap: { xxxs: 4, md: 3 },
              }}
            >
              <Stack
                sx={{
                  alignItems: 'center',
                  gap: { xxxs: 3, md: 4 },
                  minWidth: rem(200),
                }}
              >
                <Box
                  component="img"
                  src={logoWhite}
                  alt="Replify"
                  sx={{
                    height: { xxxs: rem(50), md: rem(70) },
                  }}
                />
                <Typography
                  component="h2"
                  sx={{
                    textAlign: 'center',
                    fontWeight: theme.typography.fontWeightBold,
                    fontSize: { xxxs: rem(24), md: rem(36) },
                    lineHeight: 1.2,
                    letterSpacing: rem(2),
                  }}
                >
                  Login to access these great features
                </Typography>
              </Stack>
              <Box sx={{ minWidth: rem(200) }}>
                <List>
                  {heyLibbyCapabilities.map((capability) => (
                    <ListItem
                      disablePadding
                      key={capability}
                      sx={{
                        my: 2,
                        '& .MuiListItemIcon-root': {
                          minWidth: rem(32),
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: theme.palette.primary1 }}>
                        <CheckCircle />
                      </ListItemIcon>
                      <ListItemText primary={capability} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Stack>
          </Box>
        )}
      </Stack>
      {showReplifySections && <Footer />}
    </Box>
  )
}

export default AuthLayout
